import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { RefreshCw, Trash2 } from 'lucide-react'
import { EditorContent, useEditor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import { BubbleToolbar } from './BubbleToolbar'
import { SlashMenu } from './SlashMenu'
import { buildExtensions } from './extensions'
import { createSlashMenuItems } from './slash-menu-items'
import { useImageUpload } from './useImageUpload'
import { imageFromPaste } from './imageFromPaste'
import { stripPastedImages } from './stripPastedImages'
import { setUploadPlaceholder, UploadPlaceholder, uploadPlaceholderPos } from './uploadPlaceholder'
import { UploadProgressOverlay } from '@/features/editor/components/shared/UploadProgressOverlay'
import { useSlashMenuState } from './useSlashMenuState'
import { useEditorPropSync } from './useEditorPropSync'
import { toast } from 'sonner'
import { messagesNow, useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  editable?: boolean
}

export const NotionEditor = ({ value, onChange, placeholder: own, editable = true }: Props) => {
  const m = useMessages(messages)
  const placeholder = own ?? m.placeholder
  const [linkOpenRequest, setLinkOpenRequest] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const slash = useSlashMenuState()
  const placeholderRef = useRef(placeholder)
  placeholderRef.current = placeholder
  // Сюда рисуем прогресс: узел живёт в документе на месте будущей картинки.
  const [uploadHost] = useState(() => {
    const host = document.createElement('div')
    host.contentEditable = 'false'
    // `not-prose`: стили текста редактора иначе распирали карточку, и полоска вылезала наружу.
    host.className = 'not-prose relative my-3 h-40 w-full max-w-md rounded-xl border border-border'
    return host
  })
  // Сколько картинок срезал последний разбор буфера. Тост решает `handlePaste`, который идёт
  // после разбора: копия картинки в браузере кладёт и `<img>`, и сам файл, и ошибки тут нет.
  const droppedImages = useRef(0)
  const pasteImage = useRef<(file: File) => void>(() => undefined)

  const editor = useEditor({
    extensions: [...buildExtensions(() => placeholderRef.current), UploadPlaceholder(uploadHost)],
    content: value || '<p></p>',
    editable,
    editorProps: {
      attributes: {
        // Текст автора не переводим: перевод подменил бы его и ушёл бы в сохранение.
        translate: 'no',
        class:
          'notion-editor prose prose-lg dark:prose-invert max-w-none focus:outline-none min-h-[400px] text-foreground',
      },
      handleDOMEvents: {
        // Счёт прошлого разбора (например, брошенный HTML, до `handlePaste` не доходящий) не живёт.
        paste: () => {
          droppedImages.current = 0
          return false
        },
      },
      transformPastedHTML: (html) => {
        const result = stripPastedImages(html)
        droppedImages.current = result.removed
        return result.html
      },
      handlePaste: (_view, event) => {
        const dropped = droppedImages.current
        droppedImages.current = 0
        const file = imageFromPaste(event)
        if (file) {
          pasteImage.current(file)
          return true
        }
        // Фиксированный id: двойная вставка подряд даёт один тост, а не два.
        if (dropped > 0) {
          toast.error(messagesNow(messages).pastedImagesDropped, { id: 'pasted-images-dropped' })
        }
        return false
      },
      handleKeyDown: (view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          const { from, to } = view.state.selection
          if (from !== to) {
            event.preventDefault()
            setLinkOpenRequest((x) => x + 1)
            return true
          }
        }
        if (event.key === '/' && !event.ctrlKey && !event.metaKey) {
          setTimeout(() => {
            const { from } = view.state.selection
            const coords = view.coordsAtPos(from)
            slash.setPosition({
              left: coords.left,
              cursorTop: coords.top,
              cursorBottom: coords.bottom,
            })
            slash.setFilter('')
          }, 10)
        }
        return false
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML())
      if (slash.positionRef.current) {
        const { from } = e.state.selection
        const textBefore = e.state.doc.textBetween(Math.max(0, from - 20), from, '\n')
        const slashIndex = textBefore.lastIndexOf('/')
        if (slashIndex !== -1) {
          slash.setFilter(textBefore.slice(slashIndex + 1))
        } else {
          slash.setPosition(null)
        }
      }
    },
  })

  const { fileInputRef, progress, requestUpload, requestReplace, uploadImage, handleFile } =
    useImageUpload(editor)
  pasteImage.current = uploadImage

  const slashItems = useMemo(
    () => createSlashMenuItems({ onImageRequest: requestUpload, m }),
    [requestUpload, m],
  )

  useEffect(() => {
    if (!editor) return
    if (value === editor.getHTML()) return
    // Текст пришёл извне (сохранение вернуло его нормализованным): замена документа уводит
    // курсор в конец, и вставка из меню «/» попадала туда, а не между шагами.
    const { from, to } = editor.state.selection
    const uploadPos = uploadPlaceholderPos(editor)
    editor.commands.setContent(value || '<p></p>', { emitUpdate: false })
    editor.commands.setTextSelection({ from, to })
    if (uploadPos !== null) setUploadPlaceholder(editor, uploadPos)
  }, [value, editor])

  useEditorPropSync(editor, { editable, placeholder })

  if (!editor) return null

  return (
    <div ref={containerRef} className="relative">
      {/* Текстовая панель картинке не нужна. */}
      <BubbleMenu
        editor={editor}
        options={{ shift: { padding: 8 } }}
        shouldShow={({ editor: e, from, to }) =>
          e.isEditable && e.isFocused && from !== to && !e.isActive('image')
        }
      >
        <BubbleToolbar editor={editor} linkOpenRequest={linkOpenRequest} />
      </BubbleMenu>
      <BubbleMenu
        editor={editor}
        pluginKey="imageBubbleMenu"
        options={{ shift: { padding: 8 } }}
        shouldShow={({ editor: e }) => e.isEditable && e.isActive('image')}
      >
        <div
          onMouseDown={(e) => e.preventDefault()}
          className="flex items-center gap-0.5 rounded-xl border border-border bg-popover p-1 shadow-lg"
        >
          <button
            type="button"
            onClick={requestReplace}
            className="flex h-8 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold hover:bg-foreground/10"
          >
            <RefreshCw className="size-4" />
            {m.replace}
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteSelection().run()}
            className="flex h-8 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold hover:bg-foreground/10"
          >
            <Trash2 className="size-4" />
            {m.remove}
          </button>
        </div>
      </BubbleMenu>

      <EditorContent editor={editor} />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFile}
      />

      {/* Картинка в тексте грузится — тот же вид, что у всех картинок студии, на её месте. */}
      {progress !== null
        ? createPortal(<UploadProgressOverlay progress={progress} />, uploadHost)
        : null}

      {slash.position !== null ? (
        <div className="slash-menu">
          <SlashMenu
            editor={editor}
            position={slash.position}
            filter={slash.filter}
            items={slashItems}
            onClose={slash.close}
          />
        </div>
      ) : null}
    </div>
  )
}
